import React, { PropsWithChildren, useMemo } from 'react';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { FreeItemsModalProps, Offer, ProductInterface } from '../../services/exports/Interfaces';
import useMenu from '../../hooks/menu/useMenu';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import { CompiledProduct } from '../../providers/MenuProvider';
import { Modals } from '../../modules/mobile/exports/Enums';
import classnames from 'classnames';
import { swipeableCssClass } from '../../modules/mobile/exports/Constants';
import CustomModal from '../../components-library/CustomModal';
import { MODAL_SIZES } from '../../services/exports/Constants';
import RadioButton from '../../components-library/buttons/RadioButton';
import CloseCircleIcon from '../../assets/icons/CloseCircleIcon';
import Button from '../../modules/mobile/components/buttons/Button';
import { useTranslation } from 'react-i18next';

const FreeItemsModal = (props: FreeItemsModalProps) => {
  return (
    <WithWrapper>
      <div className="flex flex-col p-lg space-y-lg h-[80dvh]">
        <Header {...props} />
        <OfferProducts offer={props.offer} />
        <Footer {...props} />
      </div>
    </WithWrapper>
  );
};

const Header = (props: FreeItemsModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:FreeItemsModal.header' });
  const { toggleFreeItemsModal } = useAppMethods();
  const toggleModal = () => toggleFreeItemsModal({ openModal: false });
  const onClose = () => {
    if (props.onClose) {
      return props.onClose();
    }

    toggleModal();
  };

  return (
    <>
      <div className="flex gap-x-2">
        <h3>{t('title')}</h3>
        <button className="ml-auto" onClick={onClose}>
          <CloseCircleIcon />
        </button>
      </div>
      <p>{t('subtitle')}</p>
    </>
  );
};

const Footer = (props: FreeItemsModalProps) => {
  const { toggleFreeItemsModal } = useAppMethods();
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:FreeItemsModal.footer' });
  const onClose = () => {
    toggleFreeItemsModal({ openModal: false });
    props.onClose && props.onClose();
  };

  return (
    <div className="!mt-auto flex space-x-md pt-md">
      <Button title={t('buttons.no_thanks')} design="secondary" className="flex-shrink-0" onClick={onClose} />
    </div>
  );
};

type OfferProductsProps = {
  offer: Offer;
};

const OfferProducts = ({ offer }: OfferProductsProps) => {
  const { compileProduct } = useMenu();
  const { toggleMenuItemModal, toggleFreeItemsModal } = useAppMethods();
  const { isMobileApp } = useMobileApp();
  const { openModal, closeModal } = useModalRouter();
  const products: CompiledProduct[] = useMemo(
    () => offer.details?.free_products?.map(compileProduct).filter(Boolean),
    [offer.details?.free_products, compileProduct],
  );
  const toggleMobileAppProduct = (product: ProductInterface) => {
    return openModal({
      id: Modals.MenuItem,
      presentation: {
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        handle: false,
        cssClass: classnames(swipeableCssClass),
      },
      props: {
        product,
        onCloseModal: closeModal,
        onBack: () =>
          openModal({
            id: Modals.FreeItemsModal,
            props: {
              offer,
            },
          }),
      },
    });
  };

  const toggleProduct = (product: ProductInterface) => {
    toggleFreeItemsModal({ openModal: false });

    if (!isMobileApp) {
      return toggleMenuItemModal({
        openModal: true,
        product: product,
        onBack: () => toggleFreeItemsModal({ openModal: true, offer }),
      });
    }

    toggleMobileAppProduct(product);
  };

  return (
    <>
      <div className="space-y-md flex flex-col overflow-y-auto">
        {products?.map((product) => (
          <button key={product.id} className="flex cursor-pointer" onClick={() => toggleProduct(product)}>
            <RadioButton isSelected={false} className="border-brand-text-secondary !size-5" innerClassName="!size-3" />
            <p className="pl-sm pb-md border-b w-full text-start">{product.name}</p>
          </button>
        ))}
      </div>
    </>
  );
};

const WithWrapper = ({ children }: PropsWithChildren) => {
  const { isMobileApp } = useMobileApp();
  const { toggleFreeItemsModal } = useAppMethods();
  const toggleModal = () => toggleFreeItemsModal({ openModal: false });

  if (isMobileApp) {
    return children;
  }

  return (
    <CustomModal open position="center" zIndex={3000} size={MODAL_SIZES.SM} onClose={toggleModal}>
      {children}
    </CustomModal>
  );
};

export default FreeItemsModal;
